/**
 * useUserMemories Hook
 * 
 * Fetches user memories from the profiles table for Phase 8a personalization.
 * These are profile-level memories that persist across all journals and sessions.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface UserMemory {
    id: string;
    content: string;
    category: 'learning_style' | 'preference' | 'fact';
    createdAt: string;
    source?: 'inferred' | 'stated';
}

/**
 * Fetch user memories from profiles.memories
 * Returns formatted memory strings for agent context
 */
export function useUserMemories() {
    const { user } = useAuth();

    return useQuery({
        queryKey: ['user-memories', user?.id],
        queryFn: async (): Promise<string[]> => {
            if (!user?.id) return [];

            const { data, error } = await supabase
                .from('profiles')
                .select('memories')
                .eq('id', user.id)
                .single();

            if (error) {
                console.error('Error fetching user memories:', error);
                return [];
            }

            // Type assertion needed until Supabase types are regenerated with new columns
            const memories = ((data as any)?.memories as UserMemory[] | null) ?? [];

            // Format memories for agent context - last 10 memories
            return memories
                .slice(-10)
                .filter((mem): mem is UserMemory =>
                    mem !== null && typeof mem === 'object' && 'content' in mem && 'category' in mem
                )
                .map(mem => `[${mem.category}] ${mem.content}`);
        },
        enabled: !!user?.id,
        staleTime: 30000, // Consider memories fresh for 30 seconds
        refetchOnWindowFocus: false,
    });
}
